import { useState, useCallback } from 'react';
import { pickModelForTask, isVisionCapableModel } from '../services/model-selector';
import { getMaxOutputTokens, callOpenRouterVision } from '../services/ai-api';
import { resolvePhotoDataUrl } from '../services/photo.js';
import { devLog } from '../utils/dev-log';
import { isTenantEmail } from '../services/rfq-requesters';
import { cachedRequesterTenantContext } from '../services/rfq-requester-tenant';

export interface PhotoOcrExtractedData {
    clientName?: string;
    clientCompany?: string;
    clientEmail?: string;
    clientPhone?: string;
    siteContactName?: string;
    siteContactPhone?: string;
    address?: string;
    strataPlan?: string;
    strataplan?: string;
    orderNumber?: string;
    jobDescription?: string;
    notes?: string;
    visitDate?: string;
    assessor?: string;
    [key: string]: string | undefined;
}

export interface PhotoOcrResult {
    hasSignificantText: boolean;
    textType: string;
    confidence: number;
    extractedData: PhotoOcrExtractedData;
    rawText: string;
    photoId?: string;
    photoName?: string;
}

export interface UsePhotoOcrOptions {
    apiKey?: string | null;
    model?: string;
    currentProjectId?: string | null;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
}

export interface UsePhotoOcrResult {
    ocrScanning: boolean;
    setOcrScanning: React.Dispatch<React.SetStateAction<boolean>>;
    ocrResults: PhotoOcrResult | null;
    setOcrResults: React.Dispatch<React.SetStateAction<PhotoOcrResult | null>>;
    ocrApplying: boolean;
    setOcrApplying: React.Dispatch<React.SetStateAction<boolean>>;
    scanPhotoForText: (photo: any) => Promise<PhotoOcrResult | null>;
    scanPhotosForOCR: (newPhotos: any[]) => Promise<void>;
    applyOcrData: () => void;
}

const OCR_PROMPT = `You are analyzing a photo that may contain a document, form, or handwritten notes for a building/construction quote.

LOOK FOR:
1. **Quote Requests (RFQ)** - Forms with fields like "Customer Details", "Job Address", "Quote Request No", "Job Description", "Site Contact", etc.
2. **Strata Plan Details** - Look specifically for "Strata Plan", "SP Number", "SP", or similar identifiers (e.g. SP12345).
3. **Inspector Notes** - Handwritten or typed notes about site conditions, defects, measurements, or observations
4. **Invoices/Quotes** - Documents with pricing, line items, company letterheads
5. **Reports** - Engineer reports, inspection documents with findings

EXTRACT ALL readable text and parse into the structured fields below.

For Quote Request forms, pay special attention to:
- Customer/Client company and contact name
- Job/Site Address (full address with suburb and postcode)
- Quote Request Number or Job Number
- Strata Plan Number (SPxxxxx)
- Site Contact name and phone for access
- Job Description or Scope of Work
- Any dates mentioned

Return ONLY a valid JSON object:
{
    "hasSignificantText": true,
    "textType": "rfq|notes|quote|invoice|document|form|none",
    "confidence": 0.0-1.0,
    "extractedData": {
        "clientName": "<contact name from ordering party>",
        "clientCompany": "<company name - strata manager, property manager, etc>",
        "clientEmail": "<email if visible>",
        "clientPhone": "<phone number>",
        "siteContactName": "<site contact for access>",
        "siteContactPhone": "<site contact phone>",
        "address": "<full job/site address including suburb and postcode>",
        "strataPlan": "<strata plan number e.g. SP12345, Strata Plan 98765>",
        "orderNumber": "<quote request number, PO, work order, job number>",
        "jobDescription": "<job description or scope of work from RFQ>",
        "notes": "<inspector observations or handwritten notes>",
        "visitDate": "<date mentioned>",
        "assessor": "<inspector name>"
    },
    "rawText": "<key text summary>"
}

IMPORTANT: 
- Extract ANY data you can read - partial data is valuable
- For addresses, include the FULL address with suburb and state
- For Quote Request forms, put the "Job Description" in "jobDescription" field
- For Handwritten/Typed Inspector Notes, put the content in "notes" field
- If you see "Access Instructions", include in notes
- ALWAYS extract the Strata Plan number if present (look for SP, Strata Plan, Plan No)
- Return confidence 0.7+ if you found useful data

If NO readable text: {"hasSignificantText": false, "textType": "none", "confidence": 0, "extractedData": {}, "rawText": ""}`;

export function usePhotoOcr({
    apiKey,
    model,
    currentProjectId,
    setFormData,
}: UsePhotoOcrOptions): UsePhotoOcrResult {
    const [ocrScanning, setOcrScanning] = useState(false);
    const [ocrResults, setOcrResults] = useState<PhotoOcrResult | null>(null);
    const [ocrApplying, setOcrApplying] = useState(false);

    // OCR: Scan photos for text content (quotes, notes, documents)
    const scanPhotoForText = useCallback(async (photo: any): Promise<PhotoOcrResult | null> => {
        if (!apiKey) return null;
        // Resolve the actual OCR model now so we can skip non-vision models
        // BEFORE making the API call. Without this, OpenRouter responds with
        // a "model does not support vision" error that was swallowed silently.
        const ocrModelResolved = pickModelForTask('image-ocr', model);
        if (!isVisionCapableModel(ocrModelResolved)) {
            console.warn('[Photo OCR] Skipped - non-vision model:', ocrModelResolved);
            return null;
        }

        try {
            // Resolve photo data URL (handles both legacy inline and blob-store photos)
            const photoDataUrl = await resolvePhotoDataUrl(photo, currentProjectId || null);
            if (!photoDataUrl) return null;

            // Route through callOpenRouterVision so this pipeline is tagged
            // with pipelineId='photo-ocr' and tracked by PipelineMonitor. The
            // previous raw-fetch path bypassed instrumentation, which is why
            // the monitor showed photo-ocr as "registered but not yet run".
            const content = await callOpenRouterVision({
                apiKey,
                model: ocrModelResolved,
                prompt: OCR_PROMPT,
                images: [{ data: photoDataUrl }],
                temperature: 0.1,
                maxTokens: getMaxOutputTokens(ocrModelResolved),
                task: 'image-ocr',
                pipelineId: 'photo-ocr',
                stageName: '1',
            } as any);

            if (content) {
                const jsonMatch = content.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                    const result = JSON.parse(jsonMatch[0]);
                    if (result.hasSignificantText && result.confidence >= 0.5) {
                        return { ...result, photoId: photo.id, photoName: photo.name };
                    }
                }
            }
        } catch (err) {
            console.error('OCR scan failed:', err);
        }
        return null;
    }, [apiKey, model, currentProjectId]);

    // Scan batch of photos for OCR
    const scanPhotosForOCR = useCallback(async (newPhotos: any[]) => {
        if (!apiKey || !Array.isArray(newPhotos) || newPhotos.length === 0) return;

        setOcrScanning(true);

        try {
            const photosToScan = newPhotos.slice(0, 5);

            for (const photo of photosToScan) {
                const result = await scanPhotoForText(photo);
                if (result && Object.keys(result.extractedData || {}).some(k => result.extractedData[k])) {
                    setOcrResults(result);
                    break;
                }
            }
        } catch (err) {
            console.error('OCR batch scan failed:', err);
        } finally {
            setOcrScanning(false);
        }
    }, [apiKey, scanPhotoForText]);

    // Apply extracted OCR data to form
    const applyOcrData = useCallback(() => {
        console.log(' applyOcrData called', { hasResults: !!ocrResults });

        if (!ocrResults?.extractedData) {
            console.warn('No OCR data to apply');
            return;
        }

        // Prevent double-firing
        if (ocrApplying) {
            console.log(' Already applying, ignoring duplicate call');
            return;
        }

        setOcrApplying(true);
        const extracted = ocrResults.extractedData;
        const photoName = ocrResults.photoName;

        // devLog: OCR extraction carries whatever the photo contained, which for
        // an RFQ or site sign is routinely client contact detail. Production
        // strips no console output, so a bare log would ship it.
        devLog('[QM] Applying OCR data:', extracted);

        let fieldsUpdated = 0;
        const tenantContext = cachedRequesterTenantContext();

        setFormData((prev: any) => {
            const updates: Record<string, any> = {};

            if (extracted.clientName && !prev?.clientName) { updates.clientName = extracted.clientName; fieldsUpdated++; }
            if (extracted.clientCompany && !prev?.clientCompany) { updates.clientCompany = extracted.clientCompany; fieldsUpdated++; }
            // A photo has no text layer to check the email against, so only
            // the builder-domain rule applies here: the builder's own address
            // is never the client's.
            if (extracted.clientEmail && !prev?.clientEmail && !isTenantEmail(extracted.clientEmail, tenantContext)) {
                updates.clientEmail = extracted.clientEmail; fieldsUpdated++;
            }
            if (extracted.clientPhone && !prev?.clientPhone) { updates.clientPhone = extracted.clientPhone; fieldsUpdated++; }
            if (extracted.siteContactName && !prev?.siteContactName) { updates.siteContactName = extracted.siteContactName; fieldsUpdated++; }
            if (extracted.siteContactPhone && !prev?.siteContactPhone) { updates.siteContactPhone = extracted.siteContactPhone; fieldsUpdated++; }
            if (extracted.address && !prev?.address) { updates.address = extracted.address; fieldsUpdated++; }

            const extractedStrata = extracted.strataPlan || extracted.strataplan;
            if (extractedStrata && !prev?.strataplan) { updates.strataplan = extractedStrata; fieldsUpdated++; }

            if (extracted.orderNumber && !prev?.orderNumber) { updates.orderNumber = extracted.orderNumber; fieldsUpdated++; }
            if (extracted.visitDate && !prev?.visitDate) { updates.visitDate = extracted.visitDate; fieldsUpdated++; }
            if (extracted.assessor && !prev?.assessor) { updates.assessor = extracted.assessor; fieldsUpdated++; }

            if (extracted.jobDescription) {
                const existingJobDesc = prev?.jobDescription || '';
                const newJobDesc = existingJobDesc
                    ? `${existingJobDesc}\n\n--- Extracted from ${photoName} ---\n${extracted.jobDescription}`
                    : extracted.jobDescription;
                updates.jobDescription = newJobDesc;
                fieldsUpdated++;
            }

            if (extracted.notes) {
                const existingNotes = prev?.notes || '';
                const newNotes = existingNotes
                    ? `${existingNotes}\n\n--- Extracted from photo: ${photoName} ---\n${extracted.notes}`
                    : extracted.notes;
                updates.notes = newNotes;
                fieldsUpdated++;
            }

            console.log(` Updated ${fieldsUpdated} fields:`, Object.keys(updates));

            return { ...(prev || {}), ...updates };
        });

        // Clear OCR results after applying
        setTimeout(() => {
            setOcrResults(null);
            setOcrApplying(false);
            console.log(' OCR data applied and cleared');
        }, 500);
    }, [ocrResults, ocrApplying, setFormData]);

    return {
        ocrScanning,
        setOcrScanning,
        ocrResults,
        setOcrResults,
        ocrApplying,
        setOcrApplying,
        scanPhotoForText,
        scanPhotosForOCR,
        applyOcrData,
    };
}
